import { portfolioApi as charterPortfolioClient } from '../../charter/api/charterApi';
import { ApiFetch } from '../../../shared/utils/fetch';
import { unwrap, type Enveloped } from '../../../shared/utils/unwrap';
import type {
  CharterMedia,
  CreatePortfolioWorkPayload,
  PortfolioSolutionId,
  PortfolioWork,
  UpdatePortfolioWorkPayload,
} from '../types';

/**
 * Per-solution portfolio client (PE-1831 follow-up). All three solutions share the same
 * portfolio-service composition underneath, but the wire paths and the media-upload
 * handshake differ:
 *  - charter      -> osprey-api `/charter/*` proxy; raw multipart upload returning `{id, cdnUrl}`
 *    directly (see charter/api/charterApi.ts) — delegated to verbatim below.
 *  - atelier      -> osprey-api `/atelier/*` wildcard proxy (atelier-proxy.controller.ts);
 *    TWO-STEP signed-URL media upload (`media/upload-url` mints a signed PUT, the browser PUTs
 *    bytes straight to GCS, then `media/confirm` registers the asset).
 *  - rack-and-box -> osprey-api's un-prefixed storefront-admin proxy (`me/portfolio*` forwarded
 *    verbatim to rack-and-box-api — see storefront-admin-proxy.controller.ts); raw multipart
 *    upload, ONE route (`me/portfolio/upload`), same shape as charter's own upload route.
 */
export interface PortfolioApi {
  list: () => Promise<PortfolioWork[]>;
  get: (id: string) => Promise<PortfolioWork>;
  create: (data: CreatePortfolioWorkPayload) => Promise<PortfolioWork>;
  update: (id: string, data: UpdatePortfolioWorkPayload) => Promise<PortfolioWork>;
  remove: (id: string) => Promise<void>;
  uploadMedia: (file: File) => Promise<CharterMedia>;
}

/**
 * media-service serves public objects at this fixed googleapis path (see
 * platform-services/media-service/src/domain/media/media.service.ts, which builds `cdnUrl` the
 * same way via `this.storage.publicUrl(objectKey, bucket)`). Unlike charter's own upload route
 * (which returns `{id, cdnUrl}` directly), atelier's and rack-and-box's media
 * upload/confirm responses only carry `{objectKey, bucket}` — no `cdnUrl` on the wire — so this
 * mirrors that exact backend convention client-side for the immediate preview thumbnail.
 */
const gcsPublicUrl = (bucket: string, objectKey: string): string =>
  `https://storage.googleapis.com/${bucket}/${objectKey}`;

// ── charter — delegates to the existing charterApi.portfolioApi verbatim ────────────────────

const CHARTER_BASE = '/charter';

const charterPortfolioApi: PortfolioApi = {
  list: () => charterPortfolioClient.list(),
  get: async (id) => unwrap(await ApiFetch.get<Enveloped<PortfolioWork>>(`${CHARTER_BASE}/me/portfolio/${id}`)),
  create: (data) => charterPortfolioClient.create(data),
  update: (id, data) => charterPortfolioClient.update(id, data),
  remove: (id) => charterPortfolioClient.remove(id),
  uploadMedia: async (file) => {
    const { id, cdnUrl } = await charterPortfolioClient.upload(file);
    return { id, cdnUrl };
  },
};

// ── atelier — /atelier/* wildcard proxy, two-step signed-URL media upload ───────────────────

const ATELIER_BASE = '/atelier';

interface AtelierUploadUrlResult {
  uploadUrl: string;
  method: 'PUT';
  objectKey: string;
  bucket: string;
  maxBytes: number;
  requiredHeaders: Record<string, string>;
}

/** The `MediaRef` shape atelier-api's + rack-and-box-api's own upload endpoints both return. */
interface MediaRefResponse {
  id: string;
  objectKey: string;
  bucket: string;
  mimeType: string;
  assetType: string;
  transcodeState?: string;
}

const atelierUploadMedia = async (file: File): Promise<CharterMedia> => {
  const mimeType = file.type || 'application/octet-stream';

  // Step 1: mint a signed PUT URL (atelier-api `POST me/portfolio/media/upload-url`).
  const minted = unwrap(
    await ApiFetch.post<Enveloped<AtelierUploadUrlResult>>(`${ATELIER_BASE}/me/portfolio/media/upload-url`, {
      filename: file.name,
      mimeType,
    }),
  );

  // Step 2: PUT the raw bytes DIRECTLY to GCS — never through osprey-api.
  const putRes = await fetch(minted.uploadUrl, {
    method: 'PUT',
    headers: minted.requiredHeaders,
    body: file,
  });
  if (!putRes.ok) throw new Error('Failed to upload file to storage');

  // Step 3: confirm — registers the asset (atelier-api `POST me/portfolio/media/confirm`).
  const confirmed = unwrap(
    await ApiFetch.post<Enveloped<MediaRefResponse>>(`${ATELIER_BASE}/me/portfolio/media/confirm`, {
      objectKey: minted.objectKey,
      bucket: minted.bucket,
      mimeType,
      sizeBytes: file.size,
    }),
  );

  return { id: confirmed.id, cdnUrl: gcsPublicUrl(confirmed.bucket, confirmed.objectKey), type: confirmed.assetType };
};

const atelierPortfolioApi: PortfolioApi = {
  list: async () => unwrap(await ApiFetch.get<Enveloped<PortfolioWork[]>>(`${ATELIER_BASE}/me/portfolio`)),
  get: async (id) => unwrap(await ApiFetch.get<Enveloped<PortfolioWork>>(`${ATELIER_BASE}/me/portfolio/${id}`)),
  create: async (data) => unwrap(await ApiFetch.post<Enveloped<PortfolioWork>>(`${ATELIER_BASE}/me/portfolio`, data)),
  update: async (id, data) =>
    unwrap(await ApiFetch.put<Enveloped<PortfolioWork>>(`${ATELIER_BASE}/me/portfolio/${id}`, data)),
  remove: async (id) => {
    await ApiFetch.del(`${ATELIER_BASE}/me/portfolio/${id}`);
  },
  uploadMedia: atelierUploadMedia,
};

// ── rack-and-box — un-prefixed proxy (forwarded verbatim), raw multipart upload ─────────────

const rackAndBoxPortfolioApi: PortfolioApi = {
  list: async () => unwrap(await ApiFetch.get<Enveloped<PortfolioWork[]>>('/me/portfolio')),
  get: async (id) => unwrap(await ApiFetch.get<Enveloped<PortfolioWork>>(`/me/portfolio/${id}`)),
  create: async (data) => unwrap(await ApiFetch.post<Enveloped<PortfolioWork>>('/me/portfolio', data)),
  update: async (id, data) => unwrap(await ApiFetch.put<Enveloped<PortfolioWork>>(`/me/portfolio/${id}`, data)),
  remove: async (id) => {
    await ApiFetch.del(`/me/portfolio/${id}`);
  },
  uploadMedia: async (file) => {
    const form = new FormData();
    form.append('file', file);
    const ref = unwrap(await ApiFetch.post<Enveloped<MediaRefResponse>>('/me/portfolio/upload', form, true));
    return { id: ref.id, cdnUrl: gcsPublicUrl(ref.bucket, ref.objectKey), type: ref.assetType };
  },
};

export const getPortfolioApi = (solutionId: PortfolioSolutionId): PortfolioApi => {
  switch (solutionId) {
    case 'charter':
      return charterPortfolioApi;
    case 'atelier':
      return atelierPortfolioApi;
    case 'rack-and-box':
      return rackAndBoxPortfolioApi;
    default: {
      const exhaustive: never = solutionId;
      throw new Error(`Unsupported portfolio solution: ${String(exhaustive)}`);
    }
  }
};
